/**
 * UnifiedSearchProgress — live 6-step progress indicator for the 360°
 * Entity Search journey. Steps animate by elapsed-time milestones (matching
 * the actual backend pipeline order). Auto-pins to the last step once the
 * job genuinely completes — caller passes ``isComplete`` to lock the final
 * tick.
 *
 * Keeps the user engaged for the ~15-25 s investigation window so they never
 * see a blank screen.
 */
import React, { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Building2, Briefcase, Globe, Scale, Gavel, Sparkles } from "lucide-react";

const STEPS = [
  { label: "Checking GST Portal…",          done: "GST Portal queried",      icon: Building2, time: 0 },
  { label: "Searching MCA21 Registry…",     done: "MCA21 indexed",           icon: Briefcase, time: 2500 },
  { label: "Scanning 200+ Sanctions Lists…", done: "Global sanctions swept",  icon: Globe,     time: 5500 },
  { label: "Checking Court Records…",       done: "eCourts / NJDG checked",  icon: Scale,     time: 8500 },
  { label: "ED / CBI / SFIO Intelligence…", done: "Enforcement records read", icon: Gavel,    time: 11500 },
  { label: "Running AI Synthesis…",         done: "Synthesis complete",      icon: Sparkles, time: 15000 },
];

export function UnifiedSearchProgress({ isRunning, isComplete }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!isRunning) return undefined;
    setStep(0);
    const timers = STEPS.map((s, i) => setTimeout(() => setStep(i), s.time));
    return () => timers.forEach(clearTimeout);
  }, [isRunning]);

  // Once backend genuinely completes, fast-forward to last step.
  useEffect(() => {
    if (isComplete) setStep(STEPS.length - 1);
  }, [isComplete]);

  if (!isRunning && !isComplete) return null;

  const active = STEPS[step] || STEPS[STEPS.length - 1];
  const ActiveIcon = active.icon;
  const pct = Math.min(100, Math.round(((step + 1) / STEPS.length) * 100));

  return (
    <div data-testid="unified-progress" style={{
      background: "linear-gradient(135deg, rgba(37, 99, 235, 0.08), rgba(29, 78, 216, 0.04))",
      border: "1px solid #1e2530",
      borderRadius: 12, padding: "18px 22px", marginBottom: 18,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <div style={{ width: 32, height: 32, borderRadius: "50%",
                      background: "rgba(37, 99, 235, 0.15)", border: "1px solid #2563eb",
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {isComplete
            ? <CheckCircle2 size={16} style={{ color: "#22c55e" }} />
            : <Loader2 size={16} className="animate-spin" style={{ color: "#60a5fa" }} />}
        </div>
        <div style={{ flex: 1 }}>
          <div data-testid="progress-label" style={{ color: "#f1f5f9", fontSize: 13, fontWeight: 700 }}>
            {isComplete ? "Investigation complete" : active.label}
          </div>
          <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 2 }}>
            Step {Math.min(step + 1, STEPS.length)} of {STEPS.length} · Rudrik 360° Intelligence Engine
          </div>
        </div>
        <div data-testid="progress-pct" style={{ color: "#60a5fa", fontSize: 18, fontWeight: 800, letterSpacing: 0.5 }}>{pct}%</div>
      </div>

      {/* Gradient bar */}
      <div style={{ height: 6, background: "#080c12", borderRadius: 4, overflow: "hidden", marginBottom: 14 }}>
        <div style={{
          width: `${pct}%`,
          height: "100%",
          background: "linear-gradient(90deg, #2563eb, #60a5fa, #22d3ee)",
          transition: "width 0.6s ease",
          boxShadow: "0 0 12px rgba(37, 99, 235, 0.6)",
        }} />
      </div>

      {/* Step dots */}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${STEPS.length}, 1fr)`, gap: 6 }}>
        {STEPS.map((s, i) => {
          const done = i < step || isComplete;
          const isActive = i === step && !isComplete;
          const Icon = s.icon;
          const c = done ? "#22c55e" : isActive ? "#60a5fa" : "#475569";
          return (
            <div key={s.label} data-testid={`progress-step-${i}`} style={{
              display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
              opacity: done || isActive ? 1 : 0.45,
              transition: "opacity 0.4s ease",
            }}>
              <Icon size={13} style={{ color: c }} />
              <span style={{ fontSize: 9, color: c, fontWeight: 700, textAlign: "center", lineHeight: 1.2, textTransform: "uppercase", letterSpacing: 0.4 }}>
                {done ? s.done : s.label.replace("…", "")}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
